import React from 'react';
import { Box, Typography, Grid, Paper, Divider, Alert } from '@mui/material';

interface Step2SimViewProps {
  data: {
    referenceYear: number;
    hasFamilyFarmersMarket: boolean | null;
    numberOfFamilyFarmers: number;
    annualMarketValue: number;
    totalFamilyFarmingValue: number;
    numberOfPermanentMarkets: number;
    numberOfNonPermanentMarkets: number;
  };
}

const InfoField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box mb={1}>
    <Typography variant="subtitle1" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body1">
      {value === null || value === undefined || value === '' ? 'Não informado' : value}
    </Typography>
  </Box>
);

const booleanText = (value: boolean | null) =>
  value === true ? 'Sim' : value === false ? 'Não' : 'Não informado';

const formatCurrency = (value: number) =>
  value ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'Não informado';

const Step2SimView: React.FC<Step2SimViewProps> = ({ data }) => (
  <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
    <Typography variant="h6" sx={{ mb: 2 }}>
      Feiras da Agricultura Familiar
    </Typography>
    <Divider sx={{ mb: 2 }} />

    <InfoField
      label="Ano de referência do Questionário"
      value={data.referenceYear}
    />

    <Grid container spacing={2}>
      <Grid item xs={12}>
        <InfoField
          label="O município possui feira da agricultura familiar?"
          value={booleanText(data.hasFamilyFarmersMarket)}
        />
      </Grid>

      {data.hasFamilyFarmersMarket && (
        <>
          <Grid item xs={12}>
            <InfoField
              label="Quantas agricultoras e agricultores familiares do município comercializam nas feiras?"
              value={data.numberOfFamilyFarmers || 'Não informado'}
            />
          </Grid>
          <Grid item xs={12}>
            <InfoField
              label="Qual o valor comercializado (R$) nas feiras do município anualmente?"
              value={formatCurrency(data.annualMarketValue)}
            />
          </Grid>
          <Grid item xs={12}>
            <InfoField
              label="Qual o valor total comercializado pela Agricultura Familiar?"
              value={formatCurrency(data.totalFamilyFarmingValue)}
            />
          </Grid>
          <Grid item xs={12}>
            <InfoField
              label="Quantas feiras permanentes regulares existem no município?"
              value={data.numberOfPermanentMarkets || 'Não informado'}
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              <strong>Feira permanente:</strong> atividade mercantil de caráter constante realizada em área pública previamente designada pela administração pública regional, com instalações comerciais fixas e edificadas para a comercialização de produtos hortifrutigranjeiros, cereais, doces, laticínios, pescados, animais vivos considerados domésticos, flores, plantas ornamentais, produtos de artesanato, lanches, caldo de cana, temperos, confecções etc.
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <InfoField
              label="Quantas feiras NÃO permanentes regulares existem no município?"
              value={data.numberOfNonPermanentMarkets || 'Não informado'}
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              <strong>Feira não permanente ou feira livre:</strong> atividade mercantil de caráter cíclico, realizada em local público previamente designado pela administração pública regional, com instalações provisórias e removíveis, podendo ocorrer em vias, logradouros públicos ou ainda em área pública coberta do tipo pavilhão.
            </Alert>
          </Grid>
        </>
      )}
    </Grid>
  </Paper>
);

export default Step2SimView;